import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ToastMessage } from 'components/common';
import openNotification from 'components/common/toastMessage/toastMessage';
import { CREATE_USER, UPDATE_USER } from 'gql/mutations';
import { GET_USER } from 'gql/queries';
import { useEffect, useState } from 'react';
import { setUser } from 'store/redux/slices/userSlices/userSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
    ErrorConstant, NFTService, Protocols, ContractUtility,
    CommonConstant
} from 'utility';

const binopolyConvertor = async (item) => {
    try {
        let tokenData;
        if (item.token_uri.startsWith("http")) {
            tokenData = await NFTService.metadataLink(item.token_uri);
        } else {
            tokenData = JSON.parse(item.token_uri.replace("data:application/json,", ""));
        }
        return {
            name: tokenData?.name,
            image_preview_url: (tokenData?.image || '').replace("ipfs://", "https://ipfs.io/ipfs/"),
            description: tokenData?.description,
            token_id: item.token_id,
            asset_contract: {
                address: item.token_address,
                name: item.name,
                schema_name: item.contract_type,
            },
            id: `${item.token_address}-${item.token_id}`,
            extraProps: tokenData.properties,
            permalink: tokenData?.external_url
        }
    } catch (error) {
        console.log(error);
        return {
            token_id: item.token_id,
            asset_contract: {
                address: item.token_address,
                name: item.name,
                schema_name: item.contract_type,
            },
            id: `${item.token_address}-${item.token_id}`
        }
    }
}


const moralisToOpenSea = async (item) => {

    if (!item.metadata && item.token_uri) {
        return await binopolyConvertor(item);
    }
    let metadata = item.metadata ? JSON.parse(item.metadata) : {};

    if (metadata?.nft?.id) {
        metadata = metadata?.nft;
    }


    const t = {
        name: metadata?.name,
        image_preview_url: (metadata?.image_url || metadata?.image || '').replace("ipfs://", "https://ipfs.io/ipfs/"),
        description: metadata?.description,
        token_id: item.token_id,
        traits: metadata?.attributes || [],
        asset_contract: {
            address: item.token_address,
            name: item.name,
            schema_name: item.contract_type,
        },
        id: `${item.token_address}-${item.token_id}`
        // permalink : 
    }
    return t;
}


export const UserNFTListHook = (protocol, address) => {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(true);
    const [filter, setFilter] = useState({
        offset: 0,
        limit: 48,
        order_direction: "desc"
    })

    useEffect(() => {

        const fetchData = async () => {
            try {
                setLoading(true);
                let result = await NFTService.userNFTs({
                    owner: address,
                    ...filter
                });

                if (filter.offset === 0) {
                    setData(result.assets || []);
                } else {
                    setData(data.concat((result.assets || [])));
                }
                setHasMore((result.assets || []).length === filter.limit);
            } catch (error) {

                console.log(error);
                setError(ErrorConstant.default)
            } finally {
                setLoading(false);
            }
        }

        const moralisFetchData = async () => {
            try {
                setLoading(true);
                let result = await NFTService.moralisNFTs(address, {
                    chain: protocol,
                    format: "decimal"
                });
           
                const ERC721Data = (result.result || []).filter((item) => item.contract_type === "ERC721")
                console.log(ERC721Data, address, "data8")
                const tempPromises = (ERC721Data || []).map(item => moralisToOpenSea(item));
                const tempResult = await Promise.all(tempPromises)
                setData(tempResult);
                setHasMore(result.total > result.page_size);
            } catch (error) {
                console.log(error);
                setError(ErrorConstant.default)
            } finally {
                setLoading(false);
            }
        }

        if (address) {
            switch (protocol) {
                case "ethereum":
                    fetchData();
                    break;
                case "rinkeby":
                    fetchData();
                    break;
                case "ropsten":
                    moralisFetchData();;
                    break;
                case "polygon":
                case Protocols.bsc.name:
                    moralisFetchData();
                    break;

                default:
                    break;
            }
        }
    }, [address, filter, protocol]);

    const pageChanged = () => {
        const temp = {
            ...filter,
            offset: filter.offset + filter.limit
        }
        setFilter({ ...temp });
    }

    return { data, loading, error, pageChanged, hasMore };
}




export const UserNFTDetailsHook = (protocol, address, tokenId, tokenAddress) => {

    const [data, setData] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { web3 } = useAppSelector(
        (state) => state.web3Connect
    );

    const fetchData = async () => {
        try {
            setLoading(true);
            const ownerAddress = await ContractUtility.getOwnerAddress(tokenId, tokenAddress, web3)
            if (address === ownerAddress) {
                let result = await NFTService.byTokenAddress({
                    owner: address,
                    token_ids: [tokenId],
                    offset: 0,
                });

                if (result.assets || [].length > 0) {
                    setData((result.assets || [])[0]);
                };
            }
            else {
                setLoading(false)
                setError("You are not the owner of this NFT")
            }

        } catch (error) {
            console.log(error);
            setError(ErrorConstant.default)
        } finally {
            setLoading(false);
        }
    }
    const moralisFetchData = async () => {
        try {
            setLoading(true);

            const ownerAddress = await ContractUtility.getOwnerAddress(tokenId, tokenAddress, web3)

            if (address === ownerAddress) {
                let result = await NFTService.moralisByTokenAddress(tokenAddress, tokenId, {
                    chain: protocol,
                    format: "decimal"
                });
                const temp = await moralisToOpenSea(result);
                setData(temp);

            }
            else {
                setLoading(false)
                setError("You are not the owner of this NFT")
            }
        } catch (error) {
            console.log(error);
            setError(ErrorConstant.default)
        } finally {
            setLoading(false);
        }
    }
    const refetch =()=>{
        if (protocol && address && tokenId && tokenAddress) {

            switch (protocol) {
                case Protocols.ethereum.name:
                    fetchData();
                    break;
                case "ropsten":
                    moralisFetchData();
                    break;
                case "polygon":
                case Protocols.bsc.name:
                    moralisFetchData();
                    break;

                default:
                    break;
            }
        }
    }


    useEffect(() => {
      
        refetch()

    }, [address, tokenId, tokenAddress, protocol]);

    return { nftData: data, nftLoading: loading, error, refetch };
}

export const MoralisCollectionHook = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const moralisFetchData = async (protocol, tokenAddress) => {
        try {
            setLoading(true);

            let result = await NFTService.moralisCollection(tokenAddress, {
                chain: protocol,
            });
            const tempPromises = (result?.result || []).map(item => moralisToOpenSea(item));
            const tempResult = await Promise.all(tempPromises)
            setData(tempResult);
        } catch (error) {
            console.log(error, "error getting collection");
            setError(ErrorConstant.default)
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        error && ToastMessage('Error', 'Something went wrong', 'error')
        data && ToastMessage('Imported', 'Collection Imported', 'success')
    }, [error, data])

    return {
        data, loading, moralisFetchData
    }

}


export const GetUserHook = (account) => {
    const { data, loading, error, refetch } = useQuery(GET_USER, { variables: { walletAddress: `${account}` }, fetchPolicy: 'network-only' });
    const { user } = useAppSelector((state) => state.user)
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (data && !error) {
            const userdata = data.get_user
            userdata && dispatch(setUser({
                ...user,
                name: userdata.name,
                bio: userdata.bio,
                profImage: userdata.prof_image,
                coverImage: userdata.cover_image,
                email: userdata.email,
                walletAddress: userdata.wallet_address,
                instagram: userdata.links.instagram,
                twitter: userdata.links.twitter,
                website: userdata.links.website,
                update: true,
                referrer: userdata.referrer,
                createdAt: userdata.createdAt
            }))
            !userdata && dispatch(setUser({
                ...user,
                walletAddress: account
            }))
        }
    }, [data])

    return {
        user, loading, error, refetch
    }

}

export const CreateUserHook = () => {
    const [create_user, { error, loading, data }] = useMutation(CREATE_USER)
    const { user } = useAppSelector(state => state.user)
    const dispatch = useAppDispatch()

    const create = (user) => {
        const { bio, name, profImage, coverImage, walletAddress, email, instagram, twitter, website, referrer } = user
        create_user({
            variables: {
                values: {
                    name,
                    profImage,
                    bio,
                    coverImage,
                    walletAddress,
                    email,
                    referrer,
                    links: {
                        instagram,
                        twitter,
                        website
                    }
                }
            }
        })
    }

    useEffect(() => {
        data && dispatch(setUser({
            ...user,
            update: true
        })) && openNotification("Created", "Profile successfully created", "success");
        error && openNotification("Error", error.message, "error");
    }, [data, error])

    return {
        data, loading, error, create
    }

}

export const UpdateUserHook = () => {
    const [update_user, { error, loading, data }] = useMutation(UPDATE_USER)

    const update = (user) => {
        const { bio, name, profImage, coverImage, walletAddress, email, instagram, twitter, website } = user
        update_user({
            variables: {
                values: {
                    name,
                    profImage,
                    bio,
                    coverImage,
                    walletAddress,
                    email,
                    links: {
                        instagram,
                        twitter,
                        website
                    }
                }
            }
        })
    }

    useEffect(() => {
        data && openNotification("Updated", "Profile successfully Updated", "success");
        error && openNotification("Error", error.message, "error");
    }, [data, error])

    return {
        data, loading, error, update
    }

}


export const GetRefreeAddressHook = () => {
    const [get_user, { data, loading, error }] = useLazyQuery(GET_USER, { fetchPolicy: 'network-only' });

    const getReferee = async (walletAddress) => {
        try {
            const result = await get_user({ variables: {walletAddress} })
            if (result.data && result.data.get_user && result.data.get_user.referrer) {
                return result.data.get_user.referrer
            }
            else return ''
        }
        catch (error) {
            console.log(error, "error in getting refree address")
            return ''
        }

    }

    return {
        getReferee, loading, error, data
    }

}